import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { ContainerField, Label, HelpText, Input, MessageErrorBox } from './style'
import inputProptypes from '@components/input/input-proptype'
import * as BaseFunctions from '../base-functions'
import { useInitialValueToValidation } from '../custom-hooks'

const InputText = ({
  // Identification Properties
  id,
  className,
  inputRef,
  internClassName,
  label,
  name,
  noLabel,
  placeholder,
  type,
  value,

  // Event and State Properties
  onChange,
  onFocus,
  onBlur,
  disabled,
  loading,
  onFieldValidation,
  onKeyUp,
  customButton,
  hasError,

  // Validate Properties
  errorMessage,
  helperText,
  required,
  requiredSymbol,
  requiredMessage,
  validMessage,
  maxLength,

  // Accessibility Properties
  tabIndex,

  // Theme Properties
  fixedLabel,
}) => {
  const [currentErrorMessage, setCurrentErrorMessage] = useState()
  const [_required, _setRequired] = useState(false)
  const [inputValue, setInputValue] = useState(value || '')
  const [isLoading, setIsLoading] = useState(loading)

  const [currentName] = useState(BaseFunctions.getInputName(name))
  const messageRequired = requiredMessage || 'Campo obrigatório'
  const _requiredSymbol = requiredSymbol || '*'
  const currentCssClass = classNames('aberto', { [`${internClassName}`]: internClassName }, { error: currentErrorMessage }, className)
  const currentType = type || 'text'

  const setInputValueWithInputType = newValue => {
    const onlyNumberRegex = /\D/g
    if (currentType === 'number' || currentType === 'tel') return setInputValue(newValue.replace(onlyNumberRegex, ''))
    setInputValue(newValue)
  }

  const sendFieldValidationForRequired = async () => {
    const hasError = validateRequiredField(value)

    await BaseFunctions.handlerOnFieldValidation(onFieldValidation, currentName, hasError)
  }

  const validateRequiredField = value => {
    const hasError = true
    if (required && !value) {
      _setRequired(true)
      return hasError
    }
    return !hasError
  }

  const validateRequiredFieldAndShowError = value => {
    const hasError = validateRequiredField(value)
    if (hasError) setCurrentErrorMessage(messageRequired)
    return hasError
  }

  const validateError = () => {
    if (hasError) setCurrentErrorMessage(errorMessage)
    return hasError
  }

  const handleOnBlur = async ({ target: { value } }) => {
    await BaseFunctions.handlerOnFieldValidation(onFieldValidation, currentName, false)
    setCurrentErrorMessage('')
    _setRequired(false)

    const fieldNeedValidation = validateRequiredFieldAndShowError(value) || validateError()
    if (fieldNeedValidation) await BaseFunctions.handlerOnFieldValidation(onFieldValidation, currentName, true)

    if (onBlur) onBlur(value)
  }

  const handleOnChange = ({ target: { value, id, name } }) => {
    setInputValueWithInputType(value)
    if (onChange) {
      setIsLoading(true)
      Promise.resolve(onChange({ value, id, name }))
        .then(result => {
          if (!result) return
          if (value.length !== result.length) setInputValue(result)
        })
        .finally(() => setIsLoading(false))
    }
  }

  const checkForErrorMessage = async () => {
    setCurrentErrorMessage('')
    await BaseFunctions.handlerOnFieldValidation(onFieldValidation, currentName, false)

    if (errorMessage) {
      setCurrentErrorMessage(errorMessage)
      await BaseFunctions.handlerOnFieldValidation(onFieldValidation, currentName, true)
    }
  }

  const containerProps = {
    isLoading,
    fixedLabel,
  }

  const inputProps = {
    type: currentType,
    id: id || currentName,
    value: inputValue,
    disabled,
    required,
    fixedLabel,
    tabindex: tabIndex || 0,
    onKeyUp,
    onChange: handleOnChange,
    onFocus,
    onBlur: handleOnBlur,
    maxLength,
    name: currentName,
    placeholder: placeholder || ' ',
    ref: inputRef,
  }

  useEffect(() => {
    const runAsync = async () => {
      await checkForErrorMessage()
    }
    runAsync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage])

  useEffect(() => {
    sendFieldValidationForRequired()
  }, [required])

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  useEffect(() => {
    setIsLoading(loading)
  }, [loading])

  useInitialValueToValidation(value, sendFieldValidationForRequired)

  return (
    <ContainerField key={id} className={currentCssClass} {...containerProps}>
      <Input {...inputProps} />
      {!noLabel && (
        <Label htmlFor={id || currentName}>
          {label}
          {required && ` ${_requiredSymbol}`}
        </Label>
      )}
      {customButton}

      {helperText && <HelpText>{helperText}</HelpText>}
      {currentErrorMessage && (
        <MessageErrorBox role="alert" id={`message-error-${id || currentName}`}>
          {currentErrorMessage}
        </MessageErrorBox>
      )}
    </ContainerField>
  )
}

InputText.propTypes = inputProptypes

export default InputText
